import React, { useState, useEffect } from 'react';
import { Modal, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { createLocalPlanPayment } from 'crud/configuration_api/local_plan_payments';
import MiniLoadingSpinner from 'components/Spinners/MiniLoadingSpinner';
import './CreateLocalPlanPayment.scss';

interface LocalPlan {
  local_plan_id: number;
  local_id: number;
  local_name: string;
  is_active: boolean;
}

interface CreateLocalPlanPaymentProps {
  show: boolean;
  onHide: () => void;
  onPaymentCreated: () => void;
  localPlans?: LocalPlan[];
  localId?: number;
  localName?: string;
}

const CreateLocalPlanPayment: React.FC<CreateLocalPlanPaymentProps> = ({ show, onHide, onPaymentCreated, localPlans = [], localId, localName }) => {
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [selectedLocalId, setSelectedLocalId] = useState<number | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [createdBy, setCreatedBy] = useState<string>('');

  // Filter only active plans
  const activePlans = localPlans.filter(plan => plan.is_active);

  useEffect(() => {
    if (show) {
      if (localId) {
        setSelectedLocalId(localId);
      }
      // Set default payment date to today
      const today = new Date().toISOString().split('T')[0];
      setPaymentDate(today);
    }
  }, [show, localId]);

  const resetForm = () => {
    if (!localId) {
      setSelectedLocalId(null);
    }
    setAmount('');
    setPaymentMethod('');
    const today = new Date().toISOString().split('T')[0];
    setPaymentDate(today);
    setNotes('');
    setCreatedBy('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const validateForm = (): boolean => {
    if (!selectedLocalId) {
      toast.error('Debes seleccionar un local');
      return false;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Debes ingresar un monto válido');
      return false;
    }
    if (!paymentDate) {
      toast.error('Debes ingresar la fecha de pago');
      return false;
    }
    if (!createdBy) {
      toast.error('Debes seleccionar quién creó el pago');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const params: any = {
        amount: parseFloat(amount),
        payment_date: paymentDate,
        created_by: createdBy
      };

      if (paymentMethod) {
        params.payment_method = paymentMethod;
      }
      if (notes) {
        params.notes = notes;
      }

      const response = await createLocalPlanPayment(selectedLocalId!, params);
      
      if ((response as any).status === 200 || (response as any).status === 201) {
        toast.success('Pago registrado exitosamente');
        onPaymentCreated();
        resetForm();
      } else {
        toast.error('Error al registrar el pago');
      }
    } catch (err: any) {
      console.error('Error creating payment:', err);
      const errorMsg = err?.response?.data?.message || 'Error al registrar el pago';
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  const paymentMethods = [
    'Efectivo',
    'Transferencia',
    'Tarjeta de Crédito',
    'Tarjeta de Débito',
    'Cheque',
    'Otro'
  ];

  const createdByOptions = [
    'Dietmar',
    'Agustin', 
    'Bastian'
  ];

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      className="local-plan-modal"
      backdrop="static"
    >
      <Modal.Header closeButton={false}>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Registrar Pago</h2>
        </div>
        <button 
          className="modal-close-button"
          onClick={handleClose}
          disabled={loading}
        >
          <X size={20} />
        </button>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {localId && localName ? (
            <div className="mp-formgroup vertical">
              <Form.Label>Local</Form.Label>
              <div className="local-plan-info">
                {localName}
              </div>
            </div>
          ) : (
            <div className="mp-formgroup vertical">
              <Form.Label>Local con Plan Activo *</Form.Label>
              {activePlans.length === 0 ? (
                <div className="no-plans-warning">
                  <p>No hay locales con planes activos. Debes crear un plan primero.</p>
                </div>
              ) : (
                <Dropdown className="local-plan-dropdown">
                  <Dropdown.Toggle 
                    variant="none" 
                    className="form-control" 
                    disabled={loading}
                    style={{ 
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    {selectedLocalId 
                      ? activePlans.find(p => p.local_id === selectedLocalId)?.local_name || 'Selecciona un local'
                      : 'Selecciona un local'
                    }
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', width: '100%', minWidth: '300px' }}>
                    {activePlans.map(plan => (
                      <Dropdown.Item 
                        key={plan.local_id} 
                        onClick={() => setSelectedLocalId(plan.local_id)}
                        active={selectedLocalId === plan.local_id}
                      >
                        {plan.local_name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
          )}

          <div className="mp-formgroup vertical">
            <Form.Label>Monto (CLP) *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ej: 50000"
              disabled={loading}
              required
              min="0"
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Fecha de Pago *</Form.Label>
            <Form.Control
              className="local-plan-input"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Método de Pago (Opcional)</Form.Label>
            <Dropdown className="local-plan-dropdown">
              <Dropdown.Toggle 
                variant="none" 
                className="form-control" 
                disabled={loading}
                style={{ 
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                {paymentMethod || 'Selecciona método de pago'}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '100%' }}>
                {paymentMethods.map((method, idx) => (
                  <Dropdown.Item 
                    key={idx} 
                    onClick={() => setPaymentMethod(method)}
                    active={paymentMethod === method}
                  >
                    {method}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Notas (Opcional)</Form.Label>
            <Form.Control
              className="local-plan-input"
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones adicionales..."
              disabled={loading}
            />
          </div>

          <div className="mp-formgroup vertical">
            <Form.Label>Creado por *</Form.Label>
            <Dropdown className="local-plan-dropdown">
              <Dropdown.Toggle 
                variant="none" 
                className="form-control" 
                disabled={loading}
                style={{ 
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                {createdBy || 'Selecciona creador'}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '100%' }}>
                {createdByOptions.map((name, idx) => (
                  <Dropdown.Item 
                    key={idx} 
                    onClick={() => setCreatedBy(name)}
                    active={createdBy === name}
                  >
                    {name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button
            type="button"
            className="btn local-plan-btn form-button-cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn local-plan-btn form-button-create"
            onClick={handleSubmit}
            disabled={loading || (!localId && activePlans.length === 0)}
          >
            {loading ? (
              <>
                <MiniLoadingSpinner />
                <span style={{ marginLeft: '0.5rem' }}>Registrando...</span>
              </>
            ) : (
              'Registrar Pago'
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateLocalPlanPayment;
